import { Close } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  FormLabel,
  Grid,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { AddShiftForm } from '@/components/forms/AddShiftForm';
import { useShiftsQuery } from '@/hooks/useShiftsQuery';
import type { Shift } from '@/openapi';
import { ScheduleService } from '@/openapi';
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dateToString } from '@/util/date';
import { queryKeys } from '@/util/queryKeys';

export interface AddShiftDialogProps {
  monday: Date;
  onClose: () => void;
}

export const AddShiftDialog: React.FC<AddShiftDialogProps> = ({
  monday,
  onClose,
}) => {
  const theme = useTheme();
  const { data: shiftsInUse } = useQuery({
    queryKey: queryKeys.shifts.byDate(monday),
    queryFn: async () => {
      const dateString = dateToString(monday);
      const assignments = await ScheduleService.scheduleServiceGetSchedule({
        query: {
          fromDate: dateString,
          toDate: dateString,
          includeUnpublished: true,
        },
      });
      return assignments.data.dates[0].assignments.map(
        (assignment) => assignment.shiftId
      );
    },
  });

  const { data: shifts } = useShiftsQuery();

  const [shiftsAvailable, setShiftsAvailable] = useState<Shift[]>([]);
  const [selectedShift, setSelectedShift] = useState<Shift | null>(null);

  useEffect(() => {
    const shiftsAvailable =
      shiftsInUse && shifts
        ? shifts
            .filter((shift) => !shiftsInUse.includes(shift.id))
            .sort((a, b) => a.rank - b.rank)
        : [];
    setShiftsAvailable(shiftsAvailable);

    if (!selectedShift && shiftsAvailable.length > 0) {
      setSelectedShift(shiftsAvailable[0]);
    }
  }, [shiftsInUse, shifts, selectedShift]);

  const shiftSelections = shiftsAvailable.map((shift) => ({
    id: shift.id,
    label: shift.description,
  }));
  const selectedShiftValue =
    shiftSelections.find(
      (shiftSelection) => shiftSelection.id === selectedShift?.id
    ) ?? null;

  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <Dialog
      open={true}
      fullWidth
      maxWidth="md"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
      }}>
      <Grid
        sx={{
          display: 'flex',
          justifyContent: 'right',
          height: theme.spacing(6),
        }}>
        <Button onClick={onClose}>
          <Close />
        </Button>
      </Grid>
      <Grid
        container
        sx={{
          px: 4,
          pb: 4,
          width: '100%',
        }}>
        <Typography variant="h4">Please select a shift to add</Typography>
        <Box
          sx={{
            p: 3,
          }}
        />
        <Grid
          container
          columns={9}
          sx={{
            alignItems: 'center',
            width: '100%',
          }}>
          <Grid
            sx={{
              textAlign: 'center',
            }}
            size={{
              xs: 12,
              md: 2,
            }}>
            <FormLabel>Shift</FormLabel>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 4,
            }}>
            <Autocomplete
              options={shiftSelections}
              value={selectedShiftValue}
              autoComplete={true}
              clearOnEscape
              onChange={(event, value) => {
                const shift =
                  shifts?.find((shift) => shift.id === value?.id) ?? null;
                setSelectedShift(shift);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus
                  inputRef={inputRef}
                  size="small"
                  sx={{ width: '100%' }}
                />
              )}
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.id}>
                    {option.label}
                  </li>
                );
              }}
              slotProps={{
                listbox: { style: { maxHeight: '10rem' } },
              }}
            />
          </Grid>
        </Grid>
        <Box
          sx={{
            p: 2,
          }}
        />
        {selectedShift && (
          <AddShiftForm
            monday={monday}
            shift={selectedShift}
            onClose={onClose}
          />
        )}
      </Grid>
    </Dialog>
  );
};
